<template>
  <div class="invoice-add-container">
    <b-row>
      <b-col :lg="12" class="pb-4">
        <div class="drip-form-container">
          <form @submit.prevent="submitInvoiceDetails" novalidate>
            <div class="drip-form">
              <h2 class="drip-h2 color-noble-blue-500">
                {{ $t('invoices.add.titles.fundingInfo') }}
              </h2>
              <div class="spacing-2"></div>
              <div class="drip-body font-weight-400">
                {{ $t('invoices.add.subTitle.fundingInfo') }}
              </div>
              <div class="spacing-32"></div>
              <div class="drip-h3 color-noble-blue-500">
                {{ $t('invoices.add.helpers.clientBankDetailId.title') }}
              </div>
              <template v-if="exporterData && exporterData.bank_details">
                <!-- Disbursement Bank Section -->
                <b-row>
                  <b-col>
                    <div class="input-container">
                      <div class="input-label">
                        {{
                          $t('invoices.add.helpers.clientBankDetailId.label')
                        }}
                      </div>
                      <div class="input-block">
                        <multiselect
                          id="client_bank_detail"
                          name="client_bank_detail"
                          data-vv-as="Disbursement Bank"
                          placeholder="Select a disbursement bank"
                          v-model="client_bank_detail"
                          v-validate="'required'"
                          class="drip-dropdown"
                          :options="renderBankDetails"
                          :option-height="150"
                          track-by="id"
                          label="dropdownLabel"
                          :show-labels="false"
                          @input="selectBank"
                          selectLabel=""
                          selectedLabel=""
                          deselectLabel=""
                          @open="isOpenDisbursement = true"
                          @close="isOpenDisbursement = false"
                        >
                          <template slot="option" slot-scope="{ option }">
                            {{ option.dropdownLabel }}
                          </template>
                          <template slot="noOptions">
                            No bank accounts linked.
                          </template>
                          <template slot="caret">
                            <img
                              v-if="isOpenDisbursement"
                              src="@/assets/images/icons/chevron-down.svg"
                              alt="caret"
                              class="action-input"
                            />
                            <img
                              v-else
                              src="@/assets/images/icons/chevron-down.svg"
                              alt="caret"
                              class="action-input inverse"
                            />
                          </template>
                        </multiselect>
                        <span
                          v-if="errors.has('client_bank_detail')"
                          class="error"
                        >
                          {{ errors.first('client_bank_detail') }}
                        </span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <!-- Funding Type -->
                <b-row>
                  <b-col>
                    <b-form-group
                      v-if="showAdjustment"
                      class="dc-group mt-4"
                      :class="{
                        'is-invalid': errors.has('invoiceType'),
                        'is-empty': invoiceType === ''
                      }"
                    >
                      <label :class="{ empty: invoiceType === '' }">
                        <h4 class="fs-20 font-weight-700 color-noble-blue-500">
                          {{ $t('invoices.add.helpers.fundingType.label') }}
                        </h4>
                      </label>
                      <b-form-radio-group
                        v-model="invoiceType"
                        name="invoiceType"
                        class="dc-radio"
                        @click.native="resetNotes"
                      >
                        <b-form-radio
                          value="fund"
                          :selected="invoiceType === 'fund'"
                        >
                          <p
                            :class="
                              invoiceType === 'fund'
                                ? 'color-black font-weight-700'
                                : 'color-black font-weight-400'
                            "
                          >
                            {{ $t('invoices.add.form.invoiceReason.funding') }}
                          </p>
                        </b-form-radio>
                        <b-form-radio
                          value="adjust"
                          :selected="invoiceType === 'adjust'"
                        >
                          <p
                            :class="
                              invoiceType === 'adjust'
                                ? 'color-black font-weight-700'
                                : 'color-black font-weight-400'
                            "
                          >
                            {{
                              $t('invoices.add.form.invoiceReason.adjustment')
                            }}
                          </p>
                        </b-form-radio>
                      </b-form-radio-group>
                    </b-form-group>
                  </b-col>
                </b-row>
                <!-- Adjustment Invoices -->
                <template v-if="invoiceType == 'adjust'">
                  <b-row>
                    <b-col>
                      <div class="input-container">
                        <div class="input-label">
                          {{
                            $t('invoices.add.helpers.adjustedInvoices.label')
                          }}
                        </div>
                        <div class="input-block">
                          <multiselect
                            v-tabindex
                            :multiple="true"
                            @input="setAdjustmentInvoices"
                            name="adjustmentInvoices"
                            class="drip-dropdown multiple-select"
                            v-validate="'required'"
                            data-vv-as="Adjustment Invoices"
                            :placeholder="
                              $t(
                                'invoices.add.helpers.adjustedInvoices.placeHolder'
                              )
                            "
                            v-model="adjustmentInvoices"
                            selectLabel=""
                            deselectLabel=""
                            selectedLabel=""
                            :options="outstandingInvoices"
                            @open="isOpenAdjustment = true"
                            @close="isOpenAdjustment = false"
                          >
                            <template slot="caret">
                              <img
                                v-if="isOpenAdjustment"
                                src="@/assets/images/icons/chevron-down.svg"
                                alt="caret"
                                class="action-input"
                              />
                              <img
                                v-else
                                src="@/assets/images/icons/chevron-down.svg"
                                alt="caret"
                                class="action-input inverse"
                              />
                            </template>
                          </multiselect>
                        </div>
                        <span
                          v-if="errors.has('adjustmentInvoices')"
                          class="error"
                        >
                          {{ errors.first('adjustmentInvoices') }}
                        </span>
                      </div>
                    </b-col>
                  </b-row>
                </template>

                <!-- Cover Letter Section && Buyer bank Section -->
                <cover-letter
                  v-if="showCoverLetterSection"
                  :coverLetterLoading="coverLetterLoading"
                  :bank-list="coverLetterData"
                  :buyer="this.selectedImporter"
                  class="mb-4"
                  @fetch-bank-list="getCoverLetterData"
                  @selected-bank-index="setSelectedBuyerBank"
                  :selected-bank="selectedBuyerBank"
                  :error="showCoverLetterError"
                />
                <!-- Bank Details Section -->
                <template>
                  <b-form-group
                    class="dc-group"
                    v-if="
                      !hideCoverLetter && Object.keys(this.bankInfo).length > 0
                    "
                  >
                    <div class="d-flex flex-column">
                      <div
                        class="fs-20 font-weight-700 color-noble-blue-500 mb-1"
                      >
                        {{ $t('buyers.single.banking.title') }}
                      </div>
                      <div class="drip-body-small font-weight-400">
                        {{ $t('buyers.single.banking.desc') }}
                      </div>
                    </div>
                  </b-form-group>
                  <div
                    class="container bank-container"
                    v-if="!hideCoverLetter && Object.keys(bankInfo).length > 0"
                  >
                    <b-row>
                      <b-col>
                        <b-row>
                          <b-col v-if="bankInfo.bank_name">
                            <div class="field-style font-weight-bold">
                              {{ $t('buyers.single.banking.fields.bankName') }}:
                              <span
                                class="field-style d-inline text-capitalize"
                              >
                                {{ bankInfo.bank_name }}
                              </span>
                            </div>
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col v-if="bankInfo.bank_address">
                            <div class="field-style font-weight-bold">
                              {{
                                $t('buyers.single.banking.fields.bankAddress')
                              }}:
                              <span
                                v-if="bankAddressType"
                                class="field-style d-inline"
                                >{{ bankInfo.bank_address }}</span
                              >
                              <span
                                v-else
                                class="field-style d-inline text-capitalize"
                                v-for="address in bankInfo.bank_address"
                                :key="address"
                                >{{ address }}</span
                              >
                            </div>
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col v-if="bankInfo.aba_routing">
                            <div class="field-style font-weight-bold">
                              {{ $t('buyers.single.banking.fields.aba') }}:
                              <span
                                class="field-style d-inline text-capitalize"
                                >{{ bankInfo.aba_routing }}</span
                              >
                            </div>
                          </b-col>
                          <b-col v-if="bankInfo.ac_name">
                            <div class="field-style font-weight-bold">
                              {{ $t('buyers.single.banking.fields.accName') }}:
                              <span
                                class="field-style d-inline text-capitalize"
                                >{{ bankInfo.ac_name }}</span
                              >
                            </div>
                          </b-col>
                        </b-row>
                        <b-row>
                          <b-col v-if="bankInfo.ac_no">
                            <div class="field-style font-weight-bold">
                              {{ $t('buyers.single.banking.fields.accNo') }}:
                              <span
                                class="field-style d-inline text-capitalize"
                                >{{ bankInfo.ac_no }}</span
                              >
                            </div>
                          </b-col>
                          <b-col v-if="bankInfo.swift">
                            <div class="field-style font-weight-bold">
                              {{ $t('buyers.single.banking.fields.swift') }}:
                              <span
                                class="field-style d-inline text-capitalize"
                                >{{ bankInfo.swift }}</span
                              >
                            </div>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </div>
                  <div v-if="invoiceType === 'fund'" class="mt-4 pt-2">
                    <b-row>
                      <b-col>
                        <b-form-group
                          class="dc-group"
                          :class="{
                            'is-invalid': errors.has('customer_notes'),
                            'is-empty': form.customer_notes === ''
                          }"
                        >
                          <label
                            class="mb-1"
                            :class="{ empty: form.customer_notes === '' }"
                          >
                            <div class="drip-body color-noble-blue-500">
                              {{
                                $t('invoices.add.helpers.customerNotes.label')
                              }}
                            </div>
                          </label>
                          <b-form-textarea
                            name="customer_notes"
                            rows="3"
                            max-rows="6"
                            no-resize
                            v-model="form.customer_notes"
                            class="funding-textarea"
                            :placeholder="
                              $t(
                                'invoices.add.helpers.customerNotes.placeHolder'
                              )
                            "
                          ></b-form-textarea>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                </template>
              </template>
              <div v-else>
                <loader :height="400" />
              </div>
            </div>
            <div class="drip-form-actions">
              <b-row>
                <b-col>
                  <b-link
                    class="mr-3 btn btn-light-purple"
                    :loading="infoSaving"
                    @click="navigateBack"
                  >
                    <span class="d-md-inline-block d-none">
                      {{ $t('invoices.add.actions.back') }}
                    </span>
                    <b-icon-chevron-left class="d-md-none" font-scale="0.9" />
                  </b-link>
                </b-col>
              </b-row>
              <br />
              <b-row>
                <b-col>
                  <b-link class="mr-3" @click="saveDraft()">
                    {{ $t('invoices.add.actions.save') }}
                  </b-link>
                  <form-button
                    :loading="infoSaving"
                    type="submit"
                    variant="secondary"
                  >
                    <span class="d-md-inline-block d-none">
                      {{ $t('invoices.add.actions.next') }}
                    </span>
                    <b-icon-chevron-right class="d-md-none" font-scale="0.9" />
                  </form-button>
                </b-col>
              </b-row>
            </div>
          </form>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import FormButton from '@/components/common/FormButton'
import Multiselect from 'vue-multiselect'
import { mapActions } from 'vuex'
import { addInvoiceMixin } from '@/mixins/Invoice'
import Loader from '@/components/common/Loader'
import buyersService from '@/services/buyers'
import invoiceService from '@/services/invoice'
import CoverLetter from '@/components/invoices/Add/Forms/funding/CoverLetter'
import userService from '@/services/user'
import exportersService from '@/services/exporters'

export default {
  components: {
    FormButton,
    Multiselect,
    Loader,
    CoverLetter
  },
  props: ['saving', 'exporterData', 'form'],
  data() {
    return {
      infoSaving: this.saving,
      nextStep: 'upload-docs',
      prevStep: 'shipping-details',
      adjustmentInvoices: [],
      invoiceType: 'fund',
      bankInfo: {},
      showCoverLetterSection: false,
      coverLetterData: [],
      selectedBuyerBank: '-1',
      showCoverLetterError: false,
      bankAccounts: [],
      coverLetterLoading: false,
      hideCoverLetter: false,
      isOpenAdjustment: false,
      isOpenDisbursement: false,
      client_bank_detail: ''
    }
  },
  created() {
    this.loadInvoice()
    this.getBankAccounts()
  },
  mounted() {
    this.$mpTrack(`Add Invoice - Funding Step Loaded`)
  },
  computed: {
    bankWatcher() {
      return { id: this.bankId, cur: this.currency }
    },
    bankId() {
      return this.selectedImporter && this.selectedImporter.id
    },
    currency() {
      return this.selectedImporter && this.selectedImporter.currency
    },
    showAdjustment() {
      return this.selectedImporter && this.outstandingInvoices.length > 0
    },
    selectedImporter() {
      return this.getBuyerFromId(this.form && this.form.importer_user_id)
    },
    outstandingInvoices() {
      return this.exporterData.outstanding_invoices
        .filter((invoice) => {
          return invoice.importer_user_id === this.form.importer_user_id
        })
        .map((invoice) => {
          return invoice.reference
        })
    },
    renderBankDetails() {
      const mergeBankDetailText = (optionSelected) => {
        if (optionSelected?.bank_name) {
          return `${optionSelected.bank_name} - ${
            optionSelected.account_number
          } - ${optionSelected.currency.toUpperCase()} - ${optionSelected.client_name.toUpperCase()}`
        }
        return ''
      }
      if (this.bankAccounts && this.exporterData?.bank_details) {
        const bankIds = this.bankAccounts.map((x) => x.id) || []
        let banks = this.exporterData.bank_details.filter((bank) =>
          bankIds.includes(bank.id)
        )
        banks = banks.map((bank) => {
          return {
            ...bank,
            dropdownLabel: mergeBankDetailText(bank)
          }
        })
        return banks
      }
      this.$mpTrack('Add Invoice - Funding details no disbursement bank found!')
      return []
    },
    bankAddressType() {
      return typeof this.bankInfo.bank_address === 'string'
    }
  },
  methods: {
    navigateBack() {
      this.$mpTrack(`Add Invoice - navigate back to ${this.prevStep}`)
      this.navigate('invoicesAdd', this.$route.params.id, this.prevStep)
    },
    getBuyerFromId(id) {
      return this.exporterData.clients_locked.find((element) => {
        return id === element.importer_user_id
      })
    },
    submitInvoiceDetails() {
      this.$mpTrack('Add Invoice - Funding Details Clicked')
      const bankOptionSelected = this.showCoverLetterSection
      this.$validator
        .validate()
        .then((result) => {
          let checkCoverLetterData = result
          if (bankOptionSelected) {
            checkCoverLetterData = result && this.validateCoverLetter()
          }
          if (checkCoverLetterData) {
            this.addFundingDetails()
          } else {
            this.$mpTrack(
              'Add Invoice - Funding validation error - mandatory fields not filled'
            )
            if (document.querySelector('.error')) {
              document.querySelector('.error').scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              })
            }
          }
        })
        .catch(() => {
          if (document.querySelector('.error')) {
            document.querySelector('.error').scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          }
        })
    },
    selectBank(bankId) {
      if (bankId) {
        this.$mpTrack('Add Invoice - Funding Details - Bank Selected', {
          bankId
        })
      }
      return false
    },
    addFundingDetails() {
      this.$mpTrack('Add Invoice - Funding Details Validated')
      this.infoSaving = true
      this.form.client_bank_detail_id = this.client_bank_detail.id

      return invoiceService
        .updateInvoices(this.$route.params.id, {
          invoice: {
            ...this.form,
            cover_letter_instruction_id:
              this.selectedBuyerBank >= 0
                ? this.coverLetterData &&
                  this.coverLetterData[this.selectedBuyerBank].id
                : ''
          }
        })
        .then(() => {
          this.infoSaving = false
          this.setFormData(this.form)
          this.setStep(this.nextStep)
          this.navigate('invoicesAdd', this.$route.params.id, this.nextStep)
          this.$mpTrack('Add Invoice - Funding Details Added')
        })
        .catch((e) => {
          this.$mpTrack('Add Invoice - Funding Details Error', {
            error: e || 'Error'
          })
          this.infoSaving = false
        })
    },
    saveDraft() {
      this.infoSaving = true
      this.form.stage = 'draft'
      return invoiceService
        .updateInvoices(this.$route.params.id, {
          invoice: this.form
        })
        .then(() => {
          this.infoSaving = false
          this.$mpTrack('Add Invoice - Funding Details Saved')
          this.navigate('invoices')
        })
        .catch(() => {
          this.infoSaving = false
        })
    },
    setAdjustmentInvoices() {
      if (this.adjustmentInvoices.length > 0) {
        this.$mpTrack(
          'Add Invoice - Funding Details - Adjustment Invoice Selected',
          {
            invoice: this.adjustmentInvoices
          }
        )
        this.form.customer_notes = `Invoices to be adjusted: ${this.adjustmentInvoices.join(
          ', '
        )}`
      } else {
        this.form.customer_notes = ''
      }
    },
    async loadInvoice() {
      const res = await invoiceService.fetchInvoicesById(
        this.$route.params?.id,
        {
          include: ['form_data']
        }
      )
      this.hideCoverLetter =
        this.$i18n.locale.includes('IN') &&
        res.data &&
        res.data['product_bank?']

      await this.setFormData(res.data.form_data)

      if (res.data['pdc_applicable?']) {
        this.$emit('get-pdc-data')
      }

      this.showCoverLetterSection =
        this.$i18n.locale.includes('IN') &&
        res.data &&
        res.data['product_bank?']
    },
    resetNotes() {
      this.adjustmentInvoices = []
      this.form.customer_notes = ''
    },
    ...mapActions(['setStep', 'setFormData']),
    fetchBankDetails() {
      return buyersService
        .fetchBuyersBankDetails(this.bankId, {
          vars: {
            currency: this.currency
          }
        })
        .then((res) => {
          this.bankInfo = res.data
        })
    },
    async getCoverLetterData() {
      if (this.showCoverLetterSection) {
        this.coverLetterLoading = true
        const res = await buyersService.fetchBuyersDetails(
          this.selectedImporter.id,
          {
            'include[]': 'cover_letter_instructions'
          }
        )
        this.coverLetterData =
          (res && res.data && res.data.cover_letter_instructions) || []

        this.coverLetterLoading = false

        if (this.coverLetterData && this.coverLetterData.length) {
          this.selectedBuyerBank = this.coverLetterData.findIndex(
            (bank) =>
              this.form.cover_letter_instruction &&
              bank.id === this.form.cover_letter_instruction.id
          )
        } else {
          this.selectedBuyerBank = '-1'
        }
      }
    },
    validateCoverLetter() {
      if (!this.$i18n.locale.includes('IN')) {
        return true
      }
      if (!this.coverLetterData.length || !(this.selectedBuyerBank >= 0)) {
        this.showCoverLetterError = true
        return false
      }
      return true
    },
    setSelectedBuyerBank(i) {
      this.selectedBuyerBank = i
    },
    getBankAccounts() {
      userService.fetchAccessToken().then((res) => {
        exportersService
          .fetchBankAccounts(
            res.data.token,
            this.$store.state.account.accountId
          )
          .then((r) => {
            const verifiedBanks = r.data['Verified']
            const unverifiedBanks = r.data['Pending Verification']
            this.bankAccounts =
              (r.data && [...verifiedBanks, ...unverifiedBanks]) || []
          })
          .catch(console.error)
      })
    }
  },
  watch: {
    bankId: {
      handler: async function (newVal) {
        if (newVal) {
          if (!this.hideCoverLetter) {
            await this.fetchBankDetails()
          }
        }
      },
      deep: true
    },
    renderBankDetails: {
      handler(val) {
        if (val?.length && this.form?.client_bank_detail_id) {
          this.client_bank_detail = val.find(
            (x) => x.id === this.form.client_bank_detail_id
          )
        }
      }
    },
    form: {
      handler(val) {
        const customerNotes = (val && val.customer_notes) || ''
        if (customerNotes.includes('Invoices to be adjusted:')) {
          this.invoiceType = 'adjust'
          this.adjustmentInvoices = val.customer_notes
            .split(':')[1]
            .split(',')
            .map((i) => i.trim())
        }
      },
      deep: true
    },
    invoiceType: {
      handler(val) {
        if (val === 'adjust') {
          this.$mpTrack(
            'Add Invoice - Funding Details - Adjustment against existing Selected'
          )
        }
        if (val === 'fund') {
          this.$mpTrack(
            'Add Invoice - Funding Details - Fund new invoice Selected'
          )
        }
      }
    },
    exporterData: {
      handler() {
        this.getCoverLetterData()
      },
      deep: true
    }
  },
  mixins: [addInvoiceMixin]
}
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
.modal-backdrop {
  opacity: 0.5 !important;
}
.funding-textarea {
  border-color: $sky-blue-400 !important;
  color: $black !important;
  font-size: 14px;
  font-weight: 400 !important;
  background: transparent !important;
  &::placeholder {
    color: $neutral-gray-500 !important;
  }
}
.custom-control-label {
  padding-left: 8px;
  padding-top: 1px;
}
.bank-container {
  background-color: $neutral-gray-50;
  border: 0.0625rem solid $neutral-gray-200;
  border-radius: 0.625rem;
  margin-left: 0px;
  padding: 1rem;
  min-width: 15rem;
}
.field-style {
  font-size: 0.8rem;
  font-family: $montserrat;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.0706rem;
  margin-bottom: 0.5rem;
}
.multiple-select {
  .multiselect__input {
    width: 0px !important;
  }
}
</style>

<style lang="scss" scoped>
.fs-20 {
  font-size: 20px;
}
.input-container {
  width: 100%;
  margin-bottom: 16px;
  .input-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
  }
  .input-block {
    display: flex;
    flex-direction: column;
    .drip-dropdown {
      height: 56px;
      width: 100%;
      .action-input {
        position: absolute;
        cursor: pointer;
        right: 0;
        padding-left: 1rem;
        padding-right: 1rem;
        line-height: 2.6rem;
        font-size: 1.2rem;
        border-bottom-right-radius: 5px;
        border-top-right-radius: 5px;
        top: 1.6rem;
        @media screen and (max-width: 768px) {
          top: 0;
        }
        width: 40px;
        &.inverse {
          transform: rotate(180deg);
        }
      }
      ::v-deep .multiselect__select {
        height: 100%;
        width: 20px;
        margin-right: 8px;
        padding: 0px;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 36px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $noble-blue-500;
        margin-bottom: 4px;
        .multiselect__tags-wrap {
          display: flex;
          overflow: scroll;
        }
        .multiselect__tag {
          position: relative;
          display: inline-block;
          padding: 6px 8px;
          padding-right: 24px;
          border-radius: 24px;
          cursor: default;
          margin-right: 10px;
          color: $noble-blue-500;
          line-height: 1;
          background: $spring-green-50;
          border: 1px solid $spring-green-400;
          margin-bottom: 5px;
          white-space: nowrap;
          overflow: hidden;
          max-width: 100%;
          min-width: 120px;
          text-overflow: ellipsis;
          .multiselect__tag-icon {
            top: 1px;
            border-radius: 50%;
            &:hover {
              background: $wealthy-green-400;
              border-radius: 50%;
            }
          }
        }
        .multiselect__placeholder {
          color: $neutral-gray-500;
          margin-top: -2px;
          padding-top: 0px;
          padding-left: 5px;
          font-size: 16px;
        }
        .multiselect__single {
          color: $noble-blue-500;
          font-weight: 700;
          vertical-align: bottom;
          width: 90% !important;
          white-space: pre;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      ::v-deep .multiselect__option {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--highlight {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--selected {
        background-color: white;
      }
      ::v-deep .multiselect__option:hover {
        background-color: $sky-blue-50;
        color: $wealthy-green-600;
      }
      ::v-deep .multiselect__content-wrapper {
        border: 1px solid $noble-blue-50;
        box-shadow: 0px 6px 12px 0px #0a2e5733;
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    input {
      outline: 0px;
      width: 400px;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      color: $noble-blue-500;
      font-size: 16px;
      font-weight: 700;
      &::placeholder {
        font-weight: 400;
        color: $neutral-gray-500;
      }
      &.width-800 {
        width: 808px;
      }
      @media screen and (max-width: 768px) {
        width: 100%;
      }
    }
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
}
</style>
